<template lang="pug">
.contact-form-wrapper
  .contact-form-container
    .success-message(v-if="isSubmitted")
      .success-icon
        svg(viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round")
          path(d="M22 11.08V12a10 10 0 1 1-5.93-9.14")
          polyline(points="22 4 12 14.01 9 11.01")
      h3 {{ labels.successTitle }}
      p {{ labels.successText }}
      button.reset-button(@click="resetFormState") {{ labels.sendAnother }}

    form.contact-form(v-else @submit.prevent="handleSubmit")
      // Salutation, First Name & Last Name
      .form-row
        .form-group.col-salutation
          label(for="contact-salutation") {{ labels.salutation }}
          select#contact-salutation(v-model="form.salutation")
            option(value="" disabled selected) {{ placeholders.selectSalutation }}
            option(v-for="opt in salutationOptions" :key="opt.value" :value="opt.value") {{ opt.label }}
        .form-group
          label(for="contact-first-name") {{ labels.firstName }} *
          input#contact-first-name(
            type="text"
            v-model.trim="form.firstName"
            :placeholder="placeholders.firstName"
            required
          )
        .form-group
          label(for="contact-last-name") {{ labels.lastName }} *
          input#contact-last-name(
            type="text"
            v-model.trim="form.lastName"
            :placeholder="placeholders.lastName"
            required
          )

      // Email & Phone
      .form-row
        .form-group
          label(for="contact-email") {{ labels.email }} *
          input#contact-email(
            type="email"
            v-model.trim="form.email"
            :placeholder="placeholders.email"
            required
          )
        .form-group
          label(for="contact-phone") {{ labels.phone }}
          input#contact-phone(
            type="tel"
            v-model.trim="form.phone"
            :placeholder="placeholders.phone"
          )

      // Company
      .form-group
        label(for="contact-company") {{ labels.company }} *
        input#contact-company(
          type="text"
          v-model.trim="form.company"
          :placeholder="placeholders.company"
          required
        )

      // Category
      .form-group
        label(for="contact-category") {{ labels.category }} *
        select#contact-category(v-model="form.category" required)
          option(v-for="cat in recipientCategories" :key="cat.id" :value="cat.id") {{ cat.label }}

      // Message
      .form-group
        label(for="contact-message") {{ labels.message }} *
        textarea#contact-message(
          v-model.trim="form.message"
          rows="5"
          :placeholder="placeholders.message"
          required
        )

      // Privacy Policy Checkbox
      .form-group.checkbox-group
        label.checkbox-label
          input(type="checkbox" v-model="form.privacy" required)
          span.checkbox-text
            | {{ labels.privacyPrefix }} 
            router-link.privacy-link(:to="privacyRoute" target="_blank") {{ labels.privacyLink }}
            | {{ labels.privacySuffix }}

      .form-error(v-if="errorMessage")
        p {{ errorMessage }}

      .form-actions
        button.submit-button(type="submit" :disabled="isSubmitting")
          span.spinner(v-if="isSubmitting")
          span {{ isSubmitting ? labels.submitting : labels.submit }}
</template>

<script>
export default {
  name: "ContactForm",
  props: {
    lang: {
      type: String,
      default: "en",
    },
  },
  data() {
    return {
      form: {
        salutation: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        category: "general",
        message: "",
        privacy: false,
      },
      isSubmitting: false,
      isSubmitted: false,
      errorMessage: "",
    };
  },
  computed: {
    privacyRoute() {
      return `/${this.lang}/impressum`;
    },
    recipientCategories() {
      if (this.lang === "de") {
        return [
          { id: "general", label: "Allgemein" },
          { id: "sales", label: "Verkauf" },
          { id: "support", label: "Technischer Support" },
          { id: "products", label: "Produkte & Lösungen" },
        ];
      }
      return [
        { id: "general", label: "General" },
        { id: "sales", label: "Sales" },
        { id: "support", label: "Technical Support" },
        { id: "products", label: "Products & Solutions" },
      ];
    },
    labels() {
      if (this.lang === "de") {
        return {
          category: "Kategorie / Anliegen",
          salutation: "Anrede",
          firstName: "Vorname",
          lastName: "Nachname",
          email: "E-Mail-Adresse",
          phone: "Telefonnummer",
          company: "Firma / Unternehmen",
          message: "Ihre Nachricht",
          privacyPrefix: "Ich stimme der Verarbeitung meiner Daten gemäß der ",
          privacyLink: "Datenschutzerklärung",
          privacySuffix: " zu.",
          submit: "Nachricht senden",
          submitting: "Wird gesendet...",
          successTitle: "Vielen Dank!",
          successText:
            "Ihre Nachricht wurde erfolgreich gesendet. Wir werden uns schnellstmöglich bei Ihnen melden.",
          sendAnother: "Weitere Nachricht senden",
        };
      }
      return {
        category: "Category",
        salutation: "Salutation",
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email Address",
        phone: "Phone Number",
        company: "Company",
        message: "Your Message",
        privacyPrefix: "I agree to the processing of my data according to the ",
        privacyLink: "Privacy Policy",
        privacySuffix: ".",
        submit: "Send Message",
        submitting: "Sending...",
        successTitle: "Thank You!",
        successText:
          "Your message has been sent successfully. We will get back to you as soon as possible.",
        sendAnother: "Send Another Message",
      };
    },
    placeholders() {
      if (this.lang === "de") {
        return {
          selectCategory: "Kategorie wählen...",
          selectSalutation: "Wählen...",
          firstName: "z. B. Max",
          lastName: "z. B. Mustermann",
          email: "z. B. max@beispiel.de",
          phone: "z. B. +49 711 123456",
          company: "z. B. Muster GmbH",
          message: "Wie können wir Ihnen helfen?",
        };
      }
      return {
        selectCategory: "Select category...",
        selectSalutation: "Select...",
        firstName: "e.g. John",
        lastName: "e.g. Doe",
        email: "e.g. john@example.com",
        phone: "e.g. +49 711 123456",
        company: "e.g. ACME Corp",
        message: "How can we help you?",
      };
    },
    salutationOptions() {
      if (this.lang === "de") {
        return [
          { value: "mr", label: "Herr" },
          { value: "ms", label: "Frau" },
          { value: "mx", label: "Divers" },
        ];
      }
      return [
        { value: "mr", label: "Mr." },
        { value: "ms", label: "Ms." },
        { value: "mx", label: "Mx." },
      ];
    },
  },
  mounted() {
    this.handlePrefill();
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.handlePrefill();
      },
    },
  },
  methods: {
    handlePrefill() {
      if (!this.$route || !this.$route.query) return;
      const { event, category, message } = this.$route.query;

      if (category && this.recipientCategories.some((c) => c.id === category)) {
        this.form.category = category;
      }

      if (event) {
        if (this.lang === "de") {
          this.form.message = `Guten Tag,\n\nich möchte gerne einen Termin auf der folgenden Veranstaltung vereinbaren:\n${event}\n\n`;
        } else {
          this.form.message = `Hello,\n\nI would like to schedule an appointment at the following event:\n${event}\n\n`;
        }
      } else if (message) {
        this.form.message = message;
      }
    },
    async handleSubmit() {
      this.errorMessage = "";
      this.isSubmitting = true;

      const payload = {
        lang: this.lang,
        category: this.form.category,
        subject: `New Contact Form Submission [${this.form.category}] - ${this.form.firstName} ${this.form.lastName}`,
        from_name: `${this.form.firstName} ${this.form.lastName}`,
        replyto: this.form.email,
        salutation: this.form.salutation,
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        email: this.form.email,
        phone: this.form.phone,
        company: this.form.company,
        message: this.form.message,
        timestamp: new Date().toISOString(),
      };

      try {
        const response = await fetch("https://www.bss-hochspannungstechnik.de/api/contactform/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          this.isSubmitting = false;
          this.isSubmitted = true;
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (err) {
        this.isSubmitting = false;
        this.errorMessage =
          this.lang === "de"
            ? "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut."
            : "An error occurred while sending your message. Please try again later.";
      }
    },
    resetFormState() {
      this.form = {
        salutation: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        category: "general",
        message: "",
        privacy: false,
      };
      this.isSubmitted = false;
      this.isSubmitting = false;
      this.errorMessage = "";
    },
  },
};
</script>

<style lang="sass" scoped>
.contact-form-wrapper
  width: 100%
  margin-top: 20px
  margin-bottom: 30px

.contact-form-container
  width: 100%
  background-color: #f8f9fa
  border: 1px solid #cbd5e1
  border-radius: 8px
  padding: 30px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04)

  @media(max-width: $mobile-breakpoint)
    padding: 20px

.contact-form
  display: flex
  flex-direction: column
  gap: 20px

.form-row
  display: flex
  gap: 20px
  width: 100%

  @media(max-width: $mobile-breakpoint)
    flex-direction: column
    gap: 20px

.form-group
  display: flex
  flex-direction: column
  flex: 1
  gap: 6px

  &.col-salutation
    flex: 0 0 120px

  @media(max-width: $mobile-breakpoint)
    &.col-salutation
      flex: 1 1 auto

  label
    font-size: 0.9em
    font-weight: 600
    color: #2c3e50

  input[type="text"],
  input[type="email"],
  input[type="tel"],
  select,
  textarea
    width: 100%
    padding: 10px 14px
    border: 1px solid #cbd5e1
    border-radius: 6px
    font-family: inherit
    font-size: 0.95em
    color: #1e293b
    background-color: #ffffff
    transition: border-color 0.2s, box-shadow 0.2s

    &:focus
      outline: none
      border-color: $color2
      box-shadow: 0 0 0 3px rgba(0, 132, 196, 0.15)

    &::placeholder
      color: #94a3b8

  select
    cursor: pointer
    appearance: auto

  textarea
    resize: vertical
    min-height: 120px

.checkbox-group
  margin-top: 5px

.checkbox-label
  display: flex
  align-items: flex-start
  gap: 10px
  font-size: 0.85em
  color: #475569
  cursor: pointer

  input[type="checkbox"]
    margin-top: 2px
    cursor: pointer
    accent-color: $color2
    width: 16px
    height: 16px

.privacy-link
  color: $color2
  text-decoration: underline
  &:hover
    color: $color3

.form-error
  background-color: #fef2f2
  border: 1px solid #fecaca
  color: #dc2626
  padding: 10px 14px
  border-radius: 6px
  font-size: 0.9em
  p
    margin: 0

.form-actions
  display: flex
  justify-content: flex-end
  margin-top: 10px

.submit-button
  display: inline-flex
  align-items: center
  justify-content: center
  gap: 8px
  background-color: $color2
  color: #ffffff
  border: none
  border-radius: 20px
  padding: 10px 24px
  font-size: 0.95em
  font-weight: 600
  cursor: pointer
  transition: background-color 0.2s, transform 0.1s, box-shadow 0.2s
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)

  &:hover:not(:disabled)
    background-color: $color3
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15)

  &:active:not(:disabled)
    transform: translateY(1px)

  &:disabled
    opacity: 0.7
    cursor: not-allowed

.spinner
  width: 14px
  height: 14px
  border: 2px solid rgba(255, 255, 255, 0.3)
  border-radius: 50%
  border-top-color: #ffffff
  animation: spin 0.8s linear infinite

@keyframes spin
  to
    transform: rotate(360deg)

.success-message
  display: flex
  flex-direction: column
  align-items: center
  text-align: center
  padding: 30px 20px

  .success-icon
    color: #10b981
    margin-bottom: 16px

  h3
    margin: 0 0 10px 0
    font-size: 1.5em
    color: #1e293b
    font-weight: 400

  p
    margin: 0 0 24px 0
    color: #64748b
    max-width: 500px

.reset-button
  background-color: transparent
  color: $color2
  border: 1px solid $color2
  border-radius: 20px
  padding: 8px 20px
  font-size: 0.9em
  font-weight: 600
  cursor: pointer
  transition: background-color 0.2s, color 0.2s

  &:hover
    background-color: $color2
    color: #ffffff
</style>
